import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock3, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const nf = (n) => Number(n).toLocaleString("it-IT", { maximumFractionDigits: 2 });

function Stat({ label, hours, count, testid }) {
  return (
    <div className="rounded-xl border border-border bg-background p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-3xl font-bold tabular-nums">{nf(hours)} <span className="text-base font-medium text-muted-foreground">h</span></p>
      <p className="text-xs text-muted-foreground">{count} servizi svolti</p>
    </div>
  );
}

export function MyHours() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/me/hours", { params: { year, month } }).then((r) => setData(r.data)).catch(() => setData(null)); }, [year, month]);

  const download = async (withMonth) => {
    try {
      const res = await api.get("/me/hours/pdf", { params: withMonth ? { year, month } : { year }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = withMonth ? `le-mie-ore-${year}-${String(month).padStart(2, "0")}.pdf` : `le-mie-ore-${year}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossibile scaricare il PDF");
    }
  };
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);
  const monthRows = (data?.services || []).filter((e) => e.date.slice(0, 7) === `${year}-${String(month).padStart(2, "0")}`);

  return (
    <section className="rounded-2xl border border-border bg-card p-6 space-y-5" data-testid="my-hours">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><Clock3 className="h-5 w-5 text-[#D32F2F]" /> Le mie ore</h2>
        <div className="flex gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-36" data-testid="my-hours-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MESI.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-24" data-testid="my-hours-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {data && (
        <div className="grid sm:grid-cols-2 gap-3">
          <Stat label={`${MESI[month - 1]} ${year}`} hours={data.month_hours} count={data.month_services} testid="my-hours-month-total" />
          <Stat label={`Anno ${year}`} hours={data.year_hours} count={data.year_services} testid="my-hours-year-total" />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => download(true)} data-testid="my-hours-pdf-month" className="rounded-full"><FileDown className="h-4 w-4 mr-1" /> PDF {MESI[month - 1]}</Button>
        <Button size="sm" variant="outline" onClick={() => download(false)} data-testid="my-hours-pdf-year" className="rounded-full"><FileDown className="h-4 w-4 mr-1" /> PDF anno {year}</Button>
      </div>
      <div>
        <p className="text-sm font-semibold mb-2">Servizi svolti a {MESI[month - 1].toLowerCase()}</p>
        {monthRows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="my-hours-empty">Nessun servizio con ore registrate in questo mese.</p>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border">
            {monthRows.map((e, i) => (
              <div key={`${e.date}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`my-hours-row-${i}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{e.event_name}</p>
                  <p className="text-xs text-muted-foreground">{new Date(e.date).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} · <span className="capitalize">{e.slot}</span></p>
                </div>
                <span className="font-semibold tabular-nums shrink-0">{nf(e.hours)} h</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
